if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', class extends HTMLElement {
    connectedCallback() {
      this.header = this.closest('.section-header');
      if (!this.header) return;
      this.alwaysSticky = ['always', 'reduce-logo-size'].includes(this.dataset.stickyType);
      this.enabled = this.dataset.stickyType !== 'none';
      this.previousScroll = window.scrollY;
      this.resize = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
      });
      this.resize.observe(this.header);
      if (this.alwaysSticky) this.header.classList.add('shopify-section-header-sticky');
      this.onScroll = () => {
        if (!this.enabled || this.querySelector('details[open]')) return;
        const scroll = window.scrollY;
        const pastHeader = scroll > this.header.offsetHeight;
        this.header.classList.toggle('scrolled-past-header', pastHeader);
        if (!this.alwaysSticky) {
          this.header.classList.toggle('shopify-section-header-sticky', pastHeader);
          this.header.classList.toggle('shopify-section-header-hidden', pastHeader && scroll > this.previousScroll);
        }
        this.previousScroll = scroll;
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
    disconnectedCallback() {
      this.resize?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
    }
  });
}

if (!customElements.get('llh-product-details')) {
  customElements.define('llh-product-details', class extends HTMLElement {
    connectedCallback() {
      const description = this.querySelector('[data-description]');
      if (description) {
        const specsHeading = Array.from(description.querySelectorAll('h2, h3, h4')).find((heading) => /^(specifications|specs)$/i.test(heading.textContent.trim()));
        if (specsHeading) {
          const specs = document.createElement('dl');
          let line = specsHeading.nextElementSibling;
          while (line && !/^H[2-4]$/.test(line.tagName)) {
            const next = line.nextElementSibling;
            const parts = line.textContent.trim().split(/\n/).filter(Boolean);
            if (parts.every((part) => part.includes(':'))) {
              parts.forEach((part) => {
                const split = part.indexOf(':');
                const label = document.createElement('dt');
                const value = document.createElement('dd');
                label.textContent = part.slice(0, split).trim();
                value.textContent = part.slice(split + 1).trim();
                specs.append(label, value);
              });
              line.remove();
            }
            line = next;
          }
          if (specs.children.length) specsHeading.after(specs);
        }
      }
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!this.tabs.length) return;
      this.querySelector('[role="tablist"]').hidden = false;
      this.querySelectorAll('.llh-product-panel__heading').forEach((heading) => { heading.hidden = true; });
      this.activate(this.tabs[0]);
      this.onClick = (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (this.tabs.includes(tab)) this.activate(tab);
      };
      this.onKeydown = (event) => {
        const index = this.tabs.indexOf(event.target);
        if (index < 0) return;
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.activate(this.tabs[next]);
        this.tabs[next].focus();
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
    }
    activate(active) {
      this.tabs.forEach((tab) => {
        const selected = tab === active;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
        panel.hidden = !selected;
        panel.setAttribute('role', 'tabpanel');
        panel.tabIndex = 0;
      });
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
    }
  });
}

if (!customElements.get('llh-product-story')) {
  customElements.define('llh-product-story', class extends HTMLElement {
    connectedCallback() {
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reveals = Array.from(this.querySelectorAll('[data-llh-reveal]'));
      this.videos = Array.from(this.querySelectorAll('video'));
      this.onMotion = () => {
        this.revealObserver?.disconnect();
        this.videoObserver?.disconnect();
        this.reveals.forEach((element) => element.classList.remove('llh-reveal-pending'));
        if (this.motion.matches || !('IntersectionObserver' in window)) {
          this.videos.forEach((video) => video.pause());
          return;
        }
        this.revealObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.remove('llh-reveal-pending');
            this.revealObserver.unobserve(entry.target);
          });
        }, { threshold: 0.08 });
        this.reveals.forEach((element) => {
          if (element.getBoundingClientRect().top >= window.innerHeight) {
            element.classList.add('llh-reveal-pending');
            this.revealObserver.observe(element);
          }
        });
        this.videoObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) entry.target.play().catch(() => {});
            else entry.target.pause();
          });
        }, { threshold: 0.35 });
        this.videos.forEach((video) => this.videoObserver.observe(video));
      };
      this.onSound = (event) => {
        const button = event.target.closest('.llh-story-video__sound');
        if (!button) return;
        const video = button.parentElement.querySelector('video');
        if (!video) return;
        video.muted = !video.muted;
        button.setAttribute('aria-pressed', String(!video.muted));
        button.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      };
      this.querySelectorAll('.llh-story-video__sound').forEach((button) => { button.hidden = false; });
      this.addEventListener('click', this.onSound);
      this.motion.addEventListener('change', this.onMotion);
      this.onMotion();
    }
    disconnectedCallback() {
      this.revealObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.motion?.removeEventListener('change', this.onMotion);
      this.removeEventListener('click', this.onSound);
      this.reveals?.forEach((element) => element.classList.remove('llh-reveal-pending'));
      this.videos?.forEach((video) => video.pause());
    }
  });
}

if (!customElements.get('llh-purchase-bar')) {
  customElements.define('llh-purchase-bar', class extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.button = this.querySelector('button');
      if (!this.productInfo || !this.button) return;
      this.desktop = window.matchMedia('(min-width: 750px)');
      this.syncPending = false;
      this.retry = () => {
        this.primary = this.productInfo.querySelector('product-form button[name="add"]');
        if (!this.primary) {
          this.retryFrame = requestAnimationFrame(this.retry);
          return;
        }
        this.initialize();
      };
      this.initialize = () => {
        if (this.initialized) return;
        this.initialized = true;
        this.hidden = false;
        this.setVisibility = (visible) => {
          this.classList.toggle('llh-purchase-bar--visible', visible);
          this.setAttribute('aria-hidden', String(!visible));
        };
        this.updateVisibility = () => {
          if (!this.primary) return;
          this.setVisibility(true);
        };
        this.sync = () => {
          const nextPrimary = this.productInfo.querySelector('product-form button[name="add"]');
          if (!nextPrimary) {
            this.primary = null;
            this.hidden = true;
            this.setVisibility(false);
            return;
          }
          this.primary = nextPrimary;
          this.hidden = false;
          const disabled = this.primary.disabled || this.primary.getAttribute('aria-disabled') === 'true';
          if (this.button.disabled !== disabled) this.button.disabled = disabled;
          const label = this.primary.querySelector('span')?.textContent.trim() || this.primary.textContent.trim();
          if (this.button.textContent.trim() !== label) this.button.textContent = label;
          const price = this.productInfo.querySelector(`[id="price-${this.dataset.section}"]`);
          const money = price?.querySelector(price.querySelector('.price--on-sale') ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular');
          const priceTarget = this.querySelector('[data-price]');
          if (money && priceTarget && priceTarget.textContent.trim() !== money.textContent.trim()) priceTarget.textContent = money.textContent.trim();
          const title = this.productInfo.querySelector('.product__title h1');
          const titleTarget = this.querySelector('[data-title]');
          if (title && titleTarget && titleTarget.textContent.trim() !== title.textContent.trim()) titleTarget.textContent = title.textContent.trim();
          const activeImage = this.productInfo.querySelector('.product__media-item.is-active img');
          const image = this.querySelector('img');
          const imageSource = activeImage?.currentSrc || activeImage?.src;
          if (image && imageSource && image.src !== imageSource) image.src = imageSource;
          this.updateVisibility();
        };
        this.scheduleSync = () => {
          if (this.syncPending) return;
          this.syncPending = true;
          queueMicrotask(() => {
            this.syncPending = false;
            this.sync();
          });
        };
        this.onClick = () => { if (!this.button.disabled) this.primary?.click(); };
        this.onViewport = () => this.updateVisibility();
        this.button.addEventListener('click', this.onClick);
        this.mutations = new MutationObserver(this.scheduleSync);
        this.mutations.observe(this.productInfo.querySelector('.product__info-container') || this.productInfo, { childList: true, subtree: true, attributes: true, characterData: true });
        window.addEventListener('scroll', this.onViewport, { passive: true });
        window.addEventListener('resize', this.onViewport);
        this.desktop.addEventListener?.('change', this.onViewport);
        this.sync();
      };
      this.retry();
    }
    disconnectedCallback() {
      if (this.retryFrame) cancelAnimationFrame(this.retryFrame);
      this.button?.removeEventListener('click', this.onClick);
      this.mutations?.disconnect();
      window.removeEventListener('scroll', this.onViewport);
      window.removeEventListener('resize', this.onViewport);
      this.desktop?.removeEventListener?.('change', this.onViewport);
    }
  });
}
